import { AloftDate, AloftDateJSON } from '@aloft/shared-objects';
import { orUndefined } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { RootState } from '../../../types.ts';
import { selectManufacturedCostApproachTableData } from '../costApproachSelectors/selectManufacturedCostApproachTableData.ts';
import {
  selectGarageCarportCost,
  selectZipOnlyGarageCarportCost,
} from '../costSelectors/selectGarageCarportCost.ts';
import {
  selectDepreciation,
  selectExternalDepreciation,
  selectFunctionalDepreciation,
  selectPhysicalDepreciation,
  selectRemainingEconomicLife,
  selectTotalEconomicLife,
} from '../depreciationSelectors.ts';
import { selectOpinionOfSiteValuePrice } from '../opinionOfSiteValueSelectors.ts';
import {
  selectCostMultiplier,
  selectEffectiveDate,
  selectPropertyType,
} from '../orderSelectors.ts';
import {
  selectAdditionalStructureTotalArea,
  selectImprovementsBasement,
  selectImprovementsById,
  selectImprovementsGarageCarport,
  selectImprovementsGla,
} from '../propertyDataSelectors/improvementsSelectors.ts';
import {
  selectAdditionalStructureCostNew,
  selectCostNew,
  selectZipOnlyAdditionalStructureCostNew,
} from '../selectCostNew.ts';
import { selectDepreciatedCostOfImprovements } from '../selectDepreciatedCostOfImprovements.ts';
import {
  selectBaseGlaAndBathCostNew,
  selectGlaAndBathCostNew,
} from '../selectGlaAndBathCostNew.ts';
import { selectSiteImprovementsRatios } from '../selectImprovementsValueRatio.ts';
import { selectSubjectValue } from '../selectSubjectValue.ts';
import { selectTotalBasementCost, selectZipOnlyBasementCost } from '../selectTotalBasementCost.ts';

export const selectAllCostApproachExportData = createSelector(
  [
    selectPropertyType,
    selectOpinionOfSiteValuePrice,
    (state: RootState) => selectImprovementsGla(state, 'subject'),
    (state: RootState) =>
      state.order.propertyType === 'manufacturedHome'
        ? selectBaseGlaAndBathCostNew(state, 'subject')
        : selectGlaAndBathCostNew(state, 'subject'),
    (state: RootState) => selectImprovementsBasement(state, 'subject'),
    (state: RootState) =>
      state.order.propertyType === 'manufacturedHome'
        ? selectZipOnlyBasementCost(state, 'subject')
        : selectTotalBasementCost(state, 'subject'),
    (state: RootState) =>
      state.order.propertyType === 'manufacturedHome'
        ? selectZipOnlyAdditionalStructureCostNew(state, 'subject')
        : selectAdditionalStructureCostNew(state, 'subject'),
    (state: RootState) => selectImprovementsGarageCarport(state, 'subject'),
    (state: RootState) =>
      state.order.propertyType === 'manufacturedHome'
        ? selectZipOnlyGarageCarportCost(state, 'subject')
        : selectGarageCarportCost(state, 'subject'),
    (state: RootState) => selectCostNew(state, 'subject'),
    (state: RootState) => selectPhysicalDepreciation(state, 'subject'),
    (state: RootState) => selectFunctionalDepreciation(state, 'subject'),
    (state: RootState) => selectExternalDepreciation(state, 'subject'),
    (state: RootState) => selectDepreciation(state, 'subject'),
    (state: RootState) => selectDepreciatedCostOfImprovements(state, 'subject'),
    selectSubjectValue,
    (state: RootState) => selectImprovementsById(state, 'subject')?.craftsmanQuality,
    (state: RootState) => selectTotalEconomicLife(state, 'subject'),
    (state: RootState) => selectImprovementsById(state, 'subject')?.effectiveAge,
    (state: RootState) => selectRemainingEconomicLife(state, 'subject'),
    selectEffectiveDate,
    (state: RootState) => selectSiteImprovementsRatios(state)?.subject,
    (state: RootState) => selectAdditionalStructureTotalArea(state, 'subject'),
    (state: RootState) =>
      // TODO: We should handle conditional cost approach export data
      // better instead of simply tacking on manufactured data
      state.order.propertyType === 'manufacturedHome'
        ? selectManufacturedCostApproachTableData(state, 'subject')
        : null,
    selectCostMultiplier,
    (state: RootState) => AloftDate.schema.parse(selectEffectiveDate(state)).getYear(),
  ],
  (
    propertyType,
    opinionOfSiteValuePrice,
    improvementsGla,
    dwellingCostNew,
    improvementsBasement,
    basementTotalCost,
    additionalStructureCostNew,
    improvementsGarageCarport,
    garageCost,
    costNew,
    physicalDepreciation,
    functionalDepreciation,
    externalDepreciation,
    depreciation,
    depreciatedCostOfImprovements,
    subjectValue,
    quality,
    totalEconomicLife,
    effectiveAge,
    remainingEconomicLife,
    effectiveDate,
    siteToImprovementRatio,
    additionalStructureTotalArea,
    manufacturedTableData,
    costMultiplier,
    effectiveDateYear,
  ) => ({
    propertyType,
    opinionOfSiteValuePrice,
    improvementsGla,
    dwellingCostNew,
    improvementsBasement,
    basementTotalCost,
    additionalStructureCostNew,
    additionalStructureTotalArea,
    improvementsGarageCarport,
    garageCost,
    costNew,
    physicalDepreciation,
    functionalDepreciation,
    externalDepreciation,
    depreciation,
    depreciatedCostOfImprovements,
    siteToImprovementRatio,
    subjectValue,
    quality,
    totalEconomicLife,
    effectiveAge,
    remainingEconomicLife,
    costMultiplier,
    source: orUndefined((date: AloftDateJSON) => ({
      name: 'Craftsman',
      effectiveDate: AloftDate.schema.parse(date),
    }))(effectiveDate),
    sections: undefined,
    manufacturedTableData,
    effectiveDateYear,
  }),
);
